'use client';

import type { HTMLAttributes } from 'react';
import React from 'react';
import classNames from 'classnames';
import once from 'lodash/once';
import { StarRating } from '$quantum/atoms/StarRating';
import { ReviewProvider } from '$constants/review-providers';
import type { CmsImage } from '$cms/types';
import { googleAnalytics4 } from '$util/analytics/googleAnalytics4';
import { Img } from '@/components/atoms/Img';
import styles from './index.module.scss';

export interface GoogleReviewsSummaryProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * @min 1
   * @max 5
   * @step 0.5
   */
  readonly rating: number;
  readonly totalReviews?: number;
  readonly basedOnText?: string;
  readonly reviewsText?: string;
  readonly link?: string;
  readonly providerLogo?: CmsImage;
  readonly providerLogoWidth?: number;
  readonly providerLogoHeight?: number;
  readonly shortVersion?: boolean;
  readonly darkTheme?: boolean;
}

const onReviewsSummaryLinkClick = once(() => {
  googleAnalytics4.googleReviewsSummaryClicked(null);
});

const renderLongVersion = ({
  rating,
  totalReviews,
  basedOnText,
  reviewsText,
  providerLogo,
  providerLogoWidth,
  providerLogoHeight,
  darkTheme,
}: GoogleReviewsSummaryProps): JSX.Element => (
  <>
    <div className={classNames(styles.scoreWrapper, darkTheme && styles.darkTheme)}>
      <p className={styles.score}>{rating.toFixed(1)}</p>
      <StarRating provider={ReviewProvider.GOOGLE} stars={rating} />
    </div>
    {totalReviews && (
      <p className={classNames(styles.reviewsText, darkTheme && styles.darkTheme)}>
        {basedOnText && <span>{basedOnText} </span>}
        {totalReviews} {reviewsText}
      </p>
    )}
    {providerLogo && (
      <Img
        className="usp-image"
        provider="cloudinary"
        src={providerLogo.url}
        alt={providerLogo.alt}
        layout="fixed"
        width={providerLogoWidth || providerLogo?.width}
        height={providerLogoHeight || providerLogo?.height}
      />
    )}
  </>
);

const renderShortVersion = ({
  providerLogo,
  providerLogoWidth,
  providerLogoHeight,
  reviewsText,
  rating,
  darkTheme,
}: GoogleReviewsSummaryProps): JSX.Element => (
  <>
    {providerLogo && (
      <Img
        className="usp-image"
        provider="cloudinary"
        src={providerLogo.url}
        alt={providerLogo.alt}
        layout="fixed"
        width={providerLogoWidth || providerLogo?.width}
        height={providerLogoHeight || providerLogo?.height}
      />
    )}
    {reviewsText && (
      <p className={classNames(styles.reviewsText, darkTheme && styles.darkTheme)}>{reviewsText}</p>
    )}
    <div className={classNames(styles.scoreWrapper, darkTheme && styles.darkTheme)}>
      <StarRating provider={ReviewProvider.GOOGLE} stars={rating} />
      <p className={styles.score}>{rating.toFixed(1)}</p>
    </div>
  </>
);

export function GoogleReviewsSummary({
  className,
  rating,
  totalReviews,
  basedOnText,
  reviewsText,
  link,
  providerLogo,
  providerLogoWidth,
  providerLogoHeight,
  shortVersion,
  darkTheme,
  ...props
}: GoogleReviewsSummaryProps): JSX.Element {
  return (
    <div className={classNames('GoogleReviewsSummary', styles.googleReviewSummary, className)} {...props}>
      <a target="_blank" href={link} rel="noreferrer" onClick={onReviewsSummaryLinkClick}>
        {shortVersion
          ? renderShortVersion({
              providerLogo,
              providerLogoWidth,
              providerLogoHeight,
              reviewsText,
              rating,
              darkTheme,
            })
          : renderLongVersion({
              rating,
              totalReviews,
              basedOnText,
              reviewsText,
              providerLogo,
              providerLogoWidth,
              providerLogoHeight,
              darkTheme,
            })}
      </a>
    </div>
  );
}
GoogleReviewsSummary.displayName = 'GoogleReviewsSummary';
